import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCms } from "../context/CmsContext";
import HeroTheaterSlideshow from "../components/HeroTheaterSlideshow";
import { getAllTheaterHeroSlides } from "../utils/theaterHeroSlides";
import { resolveCmsAssetUrl } from "../utils/cmsAssetUrl";
import "../partyBossTheme.css";

const SECTION = "mx-auto max-w-6xl";

const Home = () => {
  const { cms } = useCms();
  const h = cms.home;

  const waLink = useMemo(
    () => (text) =>
      `https://wa.me/${h.whatsappNumber}?text=${encodeURIComponent(text)}`,
    [h.whatsappNumber]
  );

  const navigate = useNavigate();
  const theaterHeroSlides = useMemo(() => getAllTheaterHeroSlides(), []);
  const heroBgVideoSrc = useMemo(
    () => resolveCmsAssetUrl(h.hero?.backgroundVideoUrl),
    [h.hero?.backgroundVideoUrl]
  );
  const heroCardVideoSrc = useMemo(
    () => resolveCmsAssetUrl(h.hero?.cardVideoUrl),
    [h.hero?.cardVideoUrl]
  );

  useEffect(() => {
    document.body.classList.add("party-boss-home");
    return () => document.body.classList.remove("party-boss-home");
  }, []);

  useEffect(() => {
    const revealOnScroll = () => {
      document.querySelectorAll(".party-boss-reveal").forEach((item) => {
        const top = item.getBoundingClientRect().top;
        if (top < window.innerHeight - 90) item.classList.add("active");
      });
    };

    revealOnScroll();
    window.addEventListener("scroll", revealOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", revealOnScroll);
  }, []);

  return (
    <div className="party-boss-page w-full overflow-x-clip bg-[#05020a] text-white selection:bg-pink-500/40">
      <div className="party-boss-bg relative z-10">
        <section
          id="home"
          className="relative min-h-0 overflow-hidden pb-16 pt-6 sm:pt-7 md:pt-8 lg:min-h-[min(100dvh,900px)] lg:pt-10"
        >
          <div className="party-boss-hero-bg absolute inset-0 -z-10 opacity-25">
            <video
              className="h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            >
              {heroBgVideoSrc ? (
                <source src={heroBgVideoSrc} />
              ) : null}
            </video>
          </div>

          <div className="relative z-[1] mx-auto grid w-full max-w-[1280px] items-start gap-10 px-4 sm:px-6 lg:grid-cols-[1.05fr_.95fr] lg:items-stretch lg:gap-12 lg:pl-[max(0.5rem,calc(1.5rem-2.25cm))] lg:pr-6">
          <div className="party-boss-reveal flex flex-col justify-center lg:min-h-0 lg:py-4">
            <div className="mb-5 inline-flex w-fit rounded-full border border-[#ffd166]/30 bg-[#ffd166]/10 px-4 py-2 text-sm font-extrabold text-[#ffd166] md:text-[15px]">
              {h.hero.badge}
            </div>
            <h1 className="mb-6 text-4xl font-black leading-[1.05] tracking-[-2px] md:text-5xl xl:text-6xl">
              {h.hero.titleBeforeGradient}{" "}
              <span className="bg-gradient-to-r from-pink-500 via-[#ffd166] to-cyan-300 bg-clip-text text-transparent">
                {h.hero.titleGradient}
              </span>
            </h1>
            <p className="mb-7 max-w-3xl text-base leading-7 text-[#c9c1d9] md:text-[17px] md:leading-8">
              {h.hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate("/booknow")}
                className="rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-6 py-3 text-sm font-black text-white shadow-2xl shadow-pink-500/30 transition hover:-translate-y-1 hover:scale-105 md:px-7 md:text-[15px]"
              >
                {h.hero.primaryCta}
              </button>
              <button
                type="button"
                onClick={() => navigate("/celebrations")}
                className="rounded-full border border-white/15 bg-white/10 px-6 py-3 text-sm font-black text-white transition hover:-translate-y-1 hover:scale-105 md:text-[15px]"
              >
                {h.hero.secondaryCta}
              </button>
            </div>
          </div>

          <div className="party-boss-reveal flex w-full flex-col items-center justify-center lg:items-end lg:justify-stretch lg:self-stretch">
            <div
              id="heroCard"
              className="party-boss-hero-card relative w-full max-w-[min(100%,440px)] overflow-hidden rounded-[26px] border border-white/15 shadow-[0_40px_100px_rgba(0,0,0,.65)] min-h-[min(56vh,440px)] lg:mx-0 lg:max-w-[min(100%,460px)] lg:min-h-[min(72vh,580px)] lg:max-h-[640px]"
            >
              <div className="absolute inset-0">
                <HeroTheaterSlideshow
                  slides={theaterHeroSlides}
                  fallbackVideoUrl={heroCardVideoSrc}
                  intervalMs={5500}
                />
              </div>
              <div className="absolute right-3 top-3 z-20 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-black text-[#ffd166] backdrop-blur-xl sm:right-4 sm:top-4 sm:px-3.5 sm:py-2 sm:text-xs md:text-[13px]">
                {h.hero.heroCardBadge}
              </div>
              <div className="absolute bottom-0 left-0 right-0 z-20 rounded-b-[26px] border-t border-white/10 bg-black/60 p-3 backdrop-blur-xl sm:p-4">
                <h2 className="mb-1 text-base font-black leading-tight sm:text-lg md:text-xl">
                  {h.hero.heroCardTitle}
                </h2>
                <p className="text-[11px] leading-snug text-white/80 sm:text-xs md:text-[13px]">
                  {h.hero.heroCardText}
                </p>
              </div>
            </div>
          </div>
          </div>
        </section>

        {/* Celebration types */}
        {(h.celebrationsIntro || h.celebrations?.length) && (
          <section
            id={h.celebrationsIntro?.id || "celebrations"}
            className="border-t border-white/10 bg-[#080512] px-[5%] py-14 lg:px-[8%] lg:py-20"
          >
            <div className={`${SECTION} party-boss-reveal text-center`}>
              {h.celebrationsIntro?.eyebrow ? (
                <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-[#ffd166]">
                  {h.celebrationsIntro.eyebrow}
                </p>
              ) : null}
              {h.celebrationsIntro?.title ? (
                <h2 className="mb-4 text-2xl font-black md:text-3xl lg:text-4xl">
                  {h.celebrationsIntro.title}
                </h2>
              ) : null}
              {h.celebrationsIntro?.text ? (
                <p className="mx-auto mb-12 max-w-2xl text-sm text-[#c9c1d9] md:text-base">
                  {h.celebrationsIntro.text}
                </p>
              ) : null}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                {(h.celebrations || []).map((c, i) => (
                  <article
                    key={`${c.title}-${i}`}
                    className="party-boss-celebration-card group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0a0614] text-left shadow-lg transition"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={resolveCmsAssetUrl(c.imageUrl)}
                        alt=""
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="relative z-[1] p-4">
                      <h3 className="mb-2 text-lg font-black text-white">
                        {c.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-[#c9c1d9]">
                        {c.text}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Why us — luxury cards */}
        {(h.luxuryIntro || h.luxuryCards?.length) && (
          <section className="border-t border-white/10 bg-[#05020a] px-[5%] py-14 lg:px-[8%] lg:py-20">
            <div className={`${SECTION} party-boss-reveal`}>
              <div className="mb-10 text-center md:mb-14">
                {h.luxuryIntro?.eyebrow ? (
                  <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-[#ffd166]">
                    {h.luxuryIntro.eyebrow}
                  </p>
                ) : null}
                {h.luxuryIntro?.title ? (
                  <h2 className="mb-4 text-2xl font-black md:text-3xl">
                    {h.luxuryIntro.title}
                  </h2>
                ) : null}
                {h.luxuryIntro?.text ? (
                  <p className="mx-auto max-w-2xl text-sm text-[#c9c1d9] md:text-base">
                    {h.luxuryIntro.text}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {(h.luxuryCards || []).map((row, i) => (
                  <div
                    key={i}
                    className="party-boss-luxury-card relative rounded-2xl border border-white/10 bg-[#0a0614] p-6 transition"
                  >
                    <div className="mb-3 text-2xl">{row[0]}</div>
                    <h3 className="mb-2 text-lg font-black">{row[1]}</h3>
                    <p className="text-sm leading-relaxed text-[#c9c1d9]">
                      {row[2]}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Food */}
        {h.foodSection && (
          <section className="border-t border-white/10 bg-[#080512] px-[5%] py-14 lg:px-[8%] lg:py-20">
            <div className={`${SECTION} party-boss-reveal grid gap-10 lg:grid-cols-2 lg:items-center`}>
              <div className="overflow-hidden rounded-2xl border border-white/10 shadow-xl">
                <img
                  src={resolveCmsAssetUrl(h.foodSection.imageUrl)}
                  alt=""
                  className="h-full min-h-[240px] w-full object-cover"
                />
              </div>
              <div>
                {h.foodSection.eyebrow ? (
                  <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-[#ffd166]">
                    {h.foodSection.eyebrow}
                  </p>
                ) : null}
                <h2 className="mb-4 text-2xl font-black md:text-3xl">
                  {h.foodSection.title}
                </h2>
                <p className="mb-6 text-sm leading-relaxed text-[#c9c1d9] md:text-base">
                  {h.foodSection.body}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(h.foodSection.tags || []).map((tag, i) => (
                    <span
                      key={i}
                      className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-[#e8e3f0]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Advantages */}
        {(h.advantagesIntro || h.advantageCards?.length) && (
          <section className="border-t border-white/10 bg-[#05020a] px-[5%] py-14 lg:px-[8%] lg:py-20">
            <div className={`${SECTION} party-boss-reveal`}>
              <div className="mb-10 text-center">
                {h.advantagesIntro?.eyebrow ? (
                  <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-[#ffd166]">
                    {h.advantagesIntro.eyebrow}
                  </p>
                ) : null}
                {h.advantagesIntro?.title ? (
                  <h2 className="mb-4 text-2xl font-black md:text-3xl">
                    {h.advantagesIntro.title}
                  </h2>
                ) : null}
                {h.advantagesIntro?.text ? (
                  <p className="mx-auto max-w-2xl text-sm text-[#c9c1d9]">
                    {h.advantagesIntro.text}
                  </p>
                ) : null}
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {(h.advantageCards || []).map((row, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#12081f] to-[#0a0614] p-6"
                  >
                    <div className="mb-2 text-2xl">{row[0]}</div>
                    <h3 className="mb-2 font-black">{row[1]}</h3>
                    <p className="text-sm text-[#c9c1d9]">{row[2]}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

       

        {/* Final CTA */}
        {h.finalCta && (
          <section
            className="relative border-t border-white/10 bg-[#080512] px-[5%] py-16 lg:px-[8%] lg:py-24"
          >
            <div
              className="party-boss-reveal absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url(${resolveCmsAssetUrl(h.finalCta.bgImageUrl)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="party-boss-reveal relative mx-auto max-w-3xl rounded-3xl border border-white/15 bg-[#05020a]/92 p-8 text-center shadow-2xl backdrop-blur-md md:p-12">
              <h2 className="mb-4 text-2xl font-black md:text-3xl">
                {h.finalCta.title}
              </h2>
              <p className="mb-8 text-[#c9c1d9]">{h.finalCta.subtitle}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/booknow")}
                  className="rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-6 py-3 text-sm font-black text-white"
                >
                  {h.finalCta.primaryLabel}
                </button>
                <a
                  href={waLink(
                    h.finalCta?.whatsappPreset || "Hi Party Boss"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-[#25D366]/60 bg-[#25D366]/15 px-6 py-3 text-sm font-black text-white"
                >
                  {h.finalCta.whatsappLabel}
                </a>
              </div>
            </div>
          </section>
        )}

        <div className="party-boss-fab-stack flex flex-col items-end gap-3">
          <a
            href={waLink(h.finalCta?.whatsappPreset || "Hi Party Boss")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-2xl text-white shadow-lg ring-2 ring-white/20 transition hover:scale-105 hover:brightness-110"
            aria-label="Chat on WhatsApp"
          >
            <i className="bi bi-whatsapp" aria-hidden />
          </a>
          <button
            type="button"
            onClick={() => navigate("/booknow")}
            className="rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-5 py-3 text-sm font-black text-white shadow-lg md:text-[15px]"
          >
            {h.floatingBookLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
